import { useEffect, type ReactNode } from "react";

export type ConfirmTone = "primary" | "danger" | "success";

const TONE_STYLES: Record<ConfirmTone, { btn: string; icon: string; iconBg: string }> = {
  primary: { btn: "bg-primary-500 hover:bg-primary-600 text-white", icon: "ri-question-line", iconBg: "bg-primary-100 text-primary-700" },
  success: { btn: "bg-primary-500 hover:bg-primary-600 text-white", icon: "ri-check-double-line", iconBg: "bg-primary-100 text-primary-700" },
  danger: { btn: "bg-red-500 hover:bg-red-600 text-white", icon: "ri-error-warning-line", iconBg: "bg-red-100 text-red-600" },
};

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  extra?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  title,
  message,
  confirmLabel = "تأكيد",
  cancelLabel = "رجوع",
  tone = "primary",
  extra,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel]);

  const styles = TONE_STYLES[tone];

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-foreground-900/50" onClick={onCancel}></div>
      <div className="relative w-full max-w-md bg-background-50 rounded-modal p-6 animate-scale-in">
        <div className="flex items-start gap-3.5">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${styles.iconBg}`}>
            <i className={`${styles.icon} text-2xl`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-foreground-900">{title}</h3>
            <p className="mt-1.5 text-sm text-foreground-500 leading-relaxed">{message}</p>
          </div>
        </div>

        {extra ? <div className="mt-4">{extra}</div> : null}

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2.5">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 h-12 rounded-btn border border-background-300 text-foreground-700 text-sm font-bold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`flex-1 h-12 rounded-btn text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${styles.btn}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}